import { Text } from '#app/components/Primitives/Text.js';
import { ThemeColorsV2 } from '#app/theme/index.js';
import {
  type LabelPosition,
  LabelVariant,
  type TextInputSize,
} from './types.js';
import { ReactNode } from 'react';
import { css, DefaultTheme, styled } from 'styled-components';

const getColor = ({
  color,
  position,
  theme,
}: {
  color?: ThemeColorsV2;
  position: LabelPosition;
  theme: DefaultTheme;
}): string => {
  if (color) {
    return theme.colorsV2[color];
  }

  if (position === 'inner') {
    return theme.colorsV2.gray60;
  }

  return theme.colorsV2.gray70;
};

const getVariantSizes = ({ variant }: { variant: LabelVariant }) => {
  switch (variant) {
    case 'hidden':
      return css`
        display: none;
      `;

    case 'subtitleSmall':
      return css`
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        letter-spacing: 0.25px;
      `;
    case 'default':

    default:
      return css`
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: 0.15px;
      `;
  }
};

const Container = styled.div<LabelProps>`
  ${({ color, position, theme, variant }) => css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    ${getVariantSizes({ variant })};
    color: ${getColor({ color, position, theme })};
  `}
`;

const Gray80Text = styled(Text)`
  color: ${({ theme }) => theme.colorsV2.gray80};
`;

type LabelProps = {
  readonly color?: ThemeColorsV2;
  readonly inputSize: TextInputSize;
  readonly position: LabelPosition;
  readonly subtitle?: string;
  readonly text?: null | ReactNode | string;
  readonly variant: LabelVariant;
};

export const Label = (props: LabelProps) => {
  const { inputSize, position, subtitle, text: label } = props;
  const showLabel =
    Boolean(label) && (position === 'outer' || inputSize !== 'small');

  if (!showLabel) {
    return null;
  }

  return (
    <Container {...props}>
      <span>{label}</span>
      {subtitle ? (
        <Gray80Text
          as="span"
          textStyle="bodySmall"
        >
          {subtitle}
        </Gray80Text>
      ) : null}
    </Container>
  );
};
